import { makeStyles } from "tss-react/mui";
import { Box, Collapse, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useStation } from "../../../providers/StationProvider";
import { useAuth } from "@emberly/zenith-client";
import NavCore from "./navcore/NavCore";
import { useNavigation } from "./NavigationProvider";
import DestinationSelector from "./common/DestinationSelector";
import TurnByTurnDisplay from "./common/TurnByTurnDisplay";
import NavigationWidget from "./common/NavigationWidget";
import GeolocationErrorDialog from "./common/GeolocationErrorDialog";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      overflow: "hidden"
    },
    map: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%"
    },
    topCenter: {
      position: "absolute",
      top: theme.spacing(2),
      left: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(2)})`,
      maxWidth: "400px",
      zIndex: 10
    },
    bottomCenter: {
      position: "absolute",
      bottom: theme.spacing(4.5),
      left: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(2)})`,
      maxWidth: "400px",
      transform: `translateY(calc(100% + ${theme.spacing(4.5)}))`,
      willChange: "transform",
      transition: "transform 0.25s !important",
      zIndex: 10
    },
    bottomCenterIn: {
      transform: "translateY(0px)"
    },
  })
);

export default function NavigationLayout(props) {
  const { description, backButton } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { station } = useStation();
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth();

  const {
    containerRef,
    setLoaded,
    setNavCore,
    cancel,
    hasDestination,
    geolocationError,
    setGeolocationError,
    setCentered,
    onRouteReported,
    onDestroy,
    suggestedDestination,
    setSuggestedDestination
  } = useNavigation();

  const hasDestinationRef = useRef(hasDestination);
  hasDestinationRef.current = hasDestination;

  const stationRef = useRef(station);
  stationRef.current = station;
  const hasStation = !!station;

  useEffect(() => {
    const container = containerRef.current;

    if (!!container && hasStation) {
      let nav = new NavCore(container, t, getAccessTokenSilently, stationRef.current);

      setNavCore(nav);

      nav.on("loaded", setLoaded);
      nav.on("cancel", cancel);
      nav.on("route", onRouteReported);

      nav.geolocation.on("geolocationerror", (err) => setGeolocationError(err));

      nav.renderer.mapbox.on("click", ev => {
        if (hasDestinationRef.current) return;
        let coords = [ev.lngLat.lng, ev.lngLat.lat];
        setSuggestedDestination(t => !t ? coords : null);
      });


      if (nav.geolocation.hasError()) {
        setGeolocationError(true);
      }

      nav.on("centered", setCentered);

      return () => {
        nav?.destroy();
        onDestroy();
      }
    }
  }, [hasStation, theme, t, getAccessTokenSilently, cancel, setLoaded, onRouteReported, onDestroy]);

  const showWidget = hasDestination || !!suggestedDestination;

  return (
    <Box className={classes.root}>

      <Collapse in={!hasDestination} className={classes.topCenter}>
        <DestinationSelector backButton={backButton} />
      </Collapse>

      <Collapse in={hasDestination} className={classes.topCenter}>
        <TurnByTurnDisplay />
      </Collapse>

      <NavigationWidget className={`${classes.bottomCenter} ${showWidget ? classes.bottomCenterIn : ""}`} description={description} />

      {!!geolocationError ? (
        <GeolocationErrorDialog />
      ) : null}

      <div className={classes.map} ref={containerRef}></div>
    </Box>
  );
}

